import React, { useRef,  useState, useEffect } from 'react';
import Topbar from '../components/Topbar';
import {
  getPickupZones,
  createPickupZone,
  updatePickupZone,
  deletePickupZone,
  getPickupZoneById
} from '../services/pickupZoneService';
import { getAirportsSelection } from '../services/poiServices';
import { toast } from 'react-toastify';
import { useRefresh } from '../utils/RefreshContext';

const PickupZones = () => {

  const { refreshKey } = useRefresh();
  const [zones, setZones] = useState([]);
  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [airportSearch, setAirportSearch] = useState('');
  const [isAirportDropdownOpen, setIsAirportDropdownOpen] = useState(false);
  const [isAirportSearching, setIsAirportSearching] = useState(false);
  const airportDropdownRef = useRef(null);

  // Filters & Pagination
  const [search, setSearch] = useState('');
  const [airportFilter, setAirportFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const limit = 10;

  const [formData, setFormData] = useState({
    zone_name: '',
    airport_id: '',
    pickup_type: 'Curbside Pickup',
    terminal: '',
    level: '',
    lat: '',
    lng: '',
    parking_cost: '',
    walk_time: '',
    google_maps_link: '',
    status: 'Active',
    notes: ''
  });

  const pickupTypes = [
    'Curbside Pickup',
    'In-Terminal Meeting Point',
    'Ride Share',
    'Taxi',
    'Shuttle'
  ];

  useEffect(() => {
    fetchAirports();
  }, []);

  const prevSearch = useRef(search);

  useEffect(() => {
    if (!isAirportSearching) return;
    if (airportSearch.length < 3) return;

    const timer = setTimeout(async () => {
      try {
        const res = await getAirportsSelection({ search: airportSearch });
        if (res && res.result) setAirports(res.data);
      } catch (err) {
        console.error(err);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [airportSearch, isAirportSearching]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (airportDropdownRef.current && !airportDropdownRef.current.contains(event.target)) {
        setIsAirportDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const searchChanged = prevSearch.current !== search;
    prevSearch.current = search;

    const timer = setTimeout(() => {
      if (searchChanged && currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchZones();
      }
    }, searchChanged ? 500 : 0);
    return () => clearTimeout(timer);
  }, [search, currentPage, airportFilter, typeFilter, statusFilter, refreshKey]);

  const fetchAirports = async () => {
    try {
      const res = await getAirportsSelection();
      if (res && res.result) setAirports(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchZones = async () => {
    setLoading(true);
    try {
      const res = await getPickupZones({
        page: currentPage,
        limit,
        search,
        airport_id: airportFilter,
        pickup_type: typeFilter,
        status: statusFilter
      });
      if (res?.data) {
        setZones(res.data || []);
        setTotalPages(res.meta?.totalPages || 1);
        setTotalRecords(res.meta?.total || 0);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch pickup zones');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (zone) => {
    setIsEditing(true);
    setCurrentId(zone.id);
    try {
      const res = await getPickupZoneById(zone.id);
      if (res?.data) {
        const selectedAirport = res.data.airport || res.data.airport_details;
        if (selectedAirport) {
          setAirportSearch(`${selectedAirport.code || ''} — ${selectedAirport.name || ''}`.replace(/^ — /, '').trim());
          setAirports(prev => {
            if (!prev.find(a => a.id === selectedAirport.id)) {
              return [selectedAirport, ...prev];
            }
            return prev;
          });
        }
        setFormData({
          zone_name: res.data.zone_name || '',
          airport_id: res.data.airport_id || '',
          pickup_type: res.data.pickup_type || 'Curbside Pickup',
          terminal: res.data.terminal || '',
          level: res.data.level || '',
          lat: res.data.lat || '',
          lng: res.data.lng || '',
          parking_cost: res.data.parking_cost || '',
          walk_time: res.data.walk_time || '',
          google_maps_link: res.data.google_maps_link || '',
          status: res.data.status || 'Active',
          notes: res.data.notes || ''
        });
        setShowForm(true);
        // Scroll to top of card where form is
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch zone details');
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await deletePickupZone(deleteId);
      toast.success('Pickup zone deleted successfully');
      setShowConfirm(false);
      setDeleteId(null);
      fetchZones();
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete pickup zone');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.zone_name || !formData.airport_id || !formData.pickup_type) {
      toast.warning('Please fill in all required fields');
      return;
    }

    try {
      let res;
      if (isEditing) {
        res = await updatePickupZone(currentId, formData);
      } else {
        res = await createPickupZone(formData);
      }

      if (res?.result === false) {
        toast.error(res.message || (isEditing ? 'Failed to update pickup zone' : 'Failed to create pickup zone'));
        return;
      }

      toast.success(isEditing ? 'Pickup zone updated successfully' : 'Pickup zone created successfully');
      resetForm();
      fetchZones();
      setShowForm(false);
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || (isEditing ? 'Failed to update pickup zone' : 'Failed to create pickup zone');
      toast.error(msg);
    }
  };

  const resetForm = () => {
    setFormData({
      zone_name: '',
      airport_id: '',
      pickup_type: 'Curbside Pickup',
      terminal: '',
      level: '',
      lat: '',
      lng: '',
      parking_cost: '',
      walk_time: '',
      google_maps_link: '',
      status: 'Active',
      notes: ''
    });
    setAirportSearch('');
    setIsAirportDropdownOpen(false);
    setIsAirportSearching(false);
    setIsEditing(false);
    setCurrentId(null);
  };

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  return (
    <div>
      <Topbar title="Pickup Zone Management" subtitle="Manage airport pickup areas, ride-share zones, and meeting points" />
      <div className="content">
        <div className="screen-label">5.5 — Pickup Zone Management</div>
        <div className="card">
          <div className="toolbar">
            <div className="search-wrap">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
              </svg>
              <input 
                className="search-input" 
                placeholder="Search pickup zones..." 
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              />
            </div>
            <select className="filter-select" value={airportFilter} onChange={(e) => { setAirportFilter(e.target.value); setCurrentPage(1); }}>
              <option value="">All Airports</option>
              {airports.map(a => <option key={a.id} value={a.id}>{a.name} ({a.code})</option>)}
            </select>
            <select className="filter-select" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}>
              <option value="">All Types</option>
              {pickupTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className="filter-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
              <option value="">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Temporarily Unavailable">Temporarily Unavailable</option>
            </select>
            <button className="btn btn-primary" onClick={() => { 
              resetForm();
              setShowForm(true);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Add Pickup Zone
            </button>
          </div>

          {/* Inline Form - Visible only when requested */}
          {showForm && (
            <div style={{ padding: '20px 26px', borderBottom: '1.5px solid #F0F4F8' }}>
              <div className="screen-label" style={{ marginBottom: 14, fontSize: 12 }}>{isEditing ? 'EDIT PICKUP ZONE' : 'ADD / EDIT PICKUP ZONE'}</div>
              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-group full">
                    <label className="form-label">ZONE NAME <span className="req">*</span></label>
                    <input 
                      className="form-input" 
                      placeholder='e.g. "Curbside Pickup Zone B"'
                      value={formData.zone_name}
                      onChange={(e) => setFormData({ ...formData, zone_name: e.target.value })}
                    />
                </div>
                <div className="form-group">
                  <label className="form-label">AIRPORT <span className="req">*</span></label>
                  <div className="searchable-select" ref={airportDropdownRef}>
                    <div className="ss-input-wrap" onClick={() => setIsAirportDropdownOpen(!isAirportDropdownOpen)}>
                      <input
                        className="form-input"
                        placeholder="Search airport..."
                        value={airportSearch}
                        onChange={(e) => {
                          setAirportSearch(e.target.value);
                          setIsAirportSearching(true);
                          setIsAirportDropdownOpen(true);
                        }}
                        onFocus={() => setIsAirportDropdownOpen(true)}
                        required
                        style={{ width: '100%' }}
                      />
                      <svg className={`ss-arrow ${isAirportDropdownOpen ? 'open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="2">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </div>
                    {isAirportDropdownOpen && (
                      <div className="ss-dropdown">
                        {airports.length > 0 ? (
                          airports.map(a => (
                            <div
                              key={a.id}
                              className={`ss-option ${String(formData.airport_id) === String(a.id) ? 'selected' : ''}`}
                              onClick={() => {
                                setFormData(prev => ({ ...prev, airport_id: a.id }));
                                setAirportSearch(`${a.code} — ${a.name}`);
                                setIsAirportSearching(false);
                                setIsAirportDropdownOpen(false);
                              }}
                            >
                              {a.code} — {a.name}
                            </div>
                          ))
                        ) : (
                          <div className="ss-no-results">No airports found</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group">
                    <label className="form-label">PICKUP TYPE <span className="req">*</span></label>
                    <select 
                      className="form-select"
                      value={formData.pickup_type}
                      onChange={(e) => setFormData({ ...formData, pickup_type: e.target.value })}
                    >
                      {pickupTypes.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">TERMINAL <span className="req">*</span></label>
                    <input 
                      className="form-input" 
                      placeholder="e.g. Terminal B, Concourse A Arrivals"
                      value={formData.terminal}
                      onChange={(e) => setFormData({ ...formData, terminal: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">LEVEL / FLOOR <span className="req">*</span></label>
                    <input 
                      className="form-input" 
                      placeholder="e.g. Level 1 Arrivals, Lower Level"
                      value={formData.level}
                      onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">LATITUDE <span className="req">*</span></label>
                    <input 
                      className="form-input" 
                      placeholder="e.g. 39.99650"
                      value={formData.lat}
                      onChange={(e) => setFormData({ ...formData, lat: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">LONGITUDE <span className="req">*</span></label>
                    <input 
                      className="form-input" 
                      placeholder="e.g. -82.88420"
                      value={formData.lng}
                      onChange={(e) => setFormData({ ...formData, lng: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">PARKING COST <span className="opt">OPTIONAL</span></label>
                    <input 
                      className="form-input" 
                      placeholder="e.g. $3/hr, Free 15 min"
                      value={formData.parking_cost}
                      onChange={(e) => setFormData({ ...formData, parking_cost: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">WALK TIME <span className="opt">OPTIONAL</span></label>
                    <input 
                      className="form-input" 
                      placeholder="e.g. 3 min walk"
                      value={formData.walk_time}
                      onChange={(e) => setFormData({ ...formData, walk_time: e.target.value })}
                    />
                  </div>
                  <div className="form-group full">
                    <label className="form-label">GOOGLE MAPS LINK <span className="opt">OPTIONAL — USED FOR OPEN IN MAPS BUTTON</span></label>
                    <input 
                      className="form-input" 
                      type="url" 
                      placeholder="https://maps.google.com/?q=..."
                      value={formData.google_maps_link}
                      onChange={(e) => setFormData({ ...formData, google_maps_link: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">STATUS <span className="req">*</span></label>
                    <select 
                      className="form-select"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="Active">Active</option>
                      <option value="Temporarily Unavailable">Temporarily Unavailable</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">NOTES <span className="opt">OPTIONAL</span></label>
                    <input 
                      className="form-input" 
                      placeholder="Internal notes for admin reference"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-footer" style={{ paddingLeft: 0, paddingRight: 0 }}>
                  <button type="button" className="btn btn-outline" onClick={() => { resetForm(); setShowForm(false); }}>Cancel</button>
                  <button type="submit" className="btn btn-primary">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                      <polyline points="17 21 17 13 7 13 7 21"/>
                    </svg>
                    {isEditing ? 'Update Pickup Zone' : 'Save Pickup Zone'}
                  </button>
                </div>
              </form>
            </div>
          )}

          <table className="tbl">
            <thead>
              <tr>
                <th>Zone Name</th>
                <th>Airport</th>
                <th>Type</th>
                <th>Terminal / Level</th>
                <th>Parking Cost</th>
                <th>Walk Time</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>Loading pickup zones...</td></tr>
              ) : zones.length === 0 ? (
                <tr><td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>No pickup zones found</td></tr>
              ) : zones.map((z) => (
                <tr key={z.id}>
                  <td style={{ fontWeight: 600, color: 'var(--navy)' }}>{z.zone_name}</td>
                  <td><span className="tag">{z.airport?.code || '—'}</span></td>
                  <td><span style={{ fontSize: 12, fontWeight: 600, color: 'var(--blue)' }}>{z.pickup_type}</span></td>
                  <td style={{ fontSize: 12, color: 'var(--grey)' }}>{z.terminal} {z.level ? `· ${z.level}` : ''}</td>
                  <td style={{ fontSize: 13, fontWeight: 600 }}>{z.parking_cost || '—'}</td>
                  <td style={{ fontSize: 13, fontWeight: 600 }}>{z.walk_time || '—'}</td>
                  <td>
                    <span className={`badge badge-${z.status === 'Active' ? 'active' : 'closed'}`}>
                      {z.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-btns">
                      <button className="action-btn" onClick={() => handleEdit(z)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button className="action-btn danger" onClick={() => handleDeleteClick(z.id)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8">
                          <path d="M3 6h18M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {zones.length > 0 && (
            <div className="pagination">
              <div className="page-info">
                Showing <b>{((currentPage - 1) * limit) + 1}</b> to <b>{Math.min(currentPage * limit, totalRecords)}</b> of <b>{totalRecords}</b> entries
              </div>
              <div className="page-btns">
                <button 
                  className="page-btn" 
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                  style={{ width: 'auto', padding: '0 12px' }}
                >
                  Prev
                </button>
                {renderPagination().map((p, i) => (
                  <button 
                    key={i} 
                    className={`page-btn ${currentPage === p ? 'active' : ''}`}
                    onClick={() => typeof p === 'number' && setCurrentPage(p)}
                    style={{ 
                      cursor: typeof p === 'number' ? 'pointer' : 'default',
                      border: typeof p === 'number' ? '' : 'none',
                      background: typeof p === 'number' ? '' : 'transparent'
                    }}
                  >
                    {p}
                  </button>
                ))}
                <button 
                  className="page-btn" 
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                  style={{ width: 'auto', padding: '0 12px' }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showConfirm && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ width: 400, textAlign: 'center', padding: '30px' }}>
            <div style={{ color: '#F43F5E', marginBottom: 20 }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <h3 style={{ marginBottom: 10, fontSize: 18, textAlign: 'center' }}>Confirm Deletion</h3>
            <p style={{ color: 'var(--grey)', marginBottom: 24, fontSize: 14, textAlign: 'center' }}>Are you sure you want to delete this pickup zone? This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button className="btn btn-outline" style={{ minWidth: 100 }} onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ background: '#F43F5E', borderColor: '#F43F5E', minWidth: 100 }} onClick={confirmDelete}>Yes, Delete It</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PickupZones;
